import React, { useState, useEffect, useContext } from 'react';
import { Button } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useEmailConfig } from './EmailConfigContext';
import { UserContext } from '../../../../../context/UserContext';
import { Rest } from '../../../../../util/rest';
import './schedule.css';

function ScheduleConfig() {
    const location = useLocation();
    const { schedule, parameters, updateSchedule } = useEmailConfig();
    const { user } = useContext(UserContext);
    const [statusBar, setStatusBar] = useState({ show: false, message: '', isSuccess: true });
    const [selectedEmail, setSelectedEmail] = useState('');
    const [availableEmails, setAvailableEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState('error'); // 'error' or 'neutral'
    const [daysOfWeek, setDaysOfWeek] = useState({
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false
    });
    const [timesPerDay, setTimesPerDay] = useState(3);
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('17:00');
    const [timezone, setTimezone] = useState('America/Puerto_Rico');
    
    const [executionDays, setExecutionDays] = useState('Monday, Tuesday, Wednesday, Thursday, Friday');
    const [executionFrequency, setExecutionFrequency] = useState('3 times per day');
    const [executionInterval, setExecutionInterval] = useState('Every 2 hours and 40 minutes');
    const [scheduledTimes, setScheduledTimes] = useState('9:00, 9:30, 10:00, 10:30, 11:00');
    const [isScheduleSettingsHelpOpen, setIsScheduleSettingsHelpOpen] = useState(false);
    const [isSchedulePreviewHelpOpen, setIsSchedulePreviewHelpOpen] = useState(false);
    
    // Update initial preview values after component mounts
    useEffect(() => {
        const initialPreview = calculateScheduledTimes('09:00', '17:00', 3);
        setExecutionInterval(initialPreview.intervalText);
        setScheduledTimes(initialPreview.scheduledTimesText);
    }, []);
    const [timezoneDisplay, setTimezoneDisplay] = useState('Puerto Rico');
    const [nextExecution, setNextExecution] = useState('Today at 09:00');

    // Default schedule parameters
    const defaultSchedule = {
        daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        timesPerDay: 2,
        startTime: '09:00',
        endTime: '17:00',
        timezone: 'America/Puerto_Rico'
    };

    // Fetch schedule data from the API
    const fetchScheduleData = async (email) => {
        if (!email) return;
        
        setLoading(true);
        setError(null);
        
        try {
            // Prepare headers with authorization
            const headers = {
                'Content-Type': 'application/json',
            };
            
            // Add authorization header if user is available
            if (user && user.tokenType && user.accessToken) {
                headers['Authorization'] = `${user.tokenType}${user.accessToken}`;
            }

            const response = await fetch(`/server/rest/ai-agent/schedule/${email}`, {
                method: 'GET',
                headers: headers,
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Parse the schedule data from the response
                let scheduleData;
                try {
                    scheduleData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
                } catch (parseError) {
                    console.error('Error parsing schedule data:', parseError);
                    setError('Failed to parse schedule data');
                    return;
                }
                
                // Update the schedule in the context
                updateSchedule(scheduleData);
                
                // Update local state with the fetched schedule data
                // Parse executionDays string to determine which days are selected
                let executionDaysArray = [];
                if (scheduleData.executionDays) {
                    try {
                        executionDaysArray = scheduleData.executionDays.split(',').map(day => day.trim().toLowerCase());
                    } catch (splitError) {
                        console.error('Error parsing execution days:', splitError);
                        executionDaysArray = [];
                    }
                }
                
                const newDaysOfWeek = {
                    monday: executionDaysArray.includes('monday'),
                    tuesday: executionDaysArray.includes('tuesday'),
                    wednesday: executionDaysArray.includes('wednesday'),
                    thursday: executionDaysArray.includes('thursday'),
                    friday: executionDaysArray.includes('friday'),
                    saturday: executionDaysArray.includes('saturday'),
                    sunday: executionDaysArray.includes('sunday')
                };
                
                // Parse working hours if available
                let startTime = '09:00';
                let endTime = '17:00';
                if (scheduleData.workingHours) {
                    const hours = scheduleData.workingHours.split(' - ');
                    if (hours.length === 2) {
                        startTime = hours[0];
                        endTime = hours[1];
                    }
                }
                
                // Get interval from API response if available, otherwise calculate it
                let interval = 'Every 2 hours and 40 minutes';
                if (scheduleData.interval) {
                    interval = scheduleData.interval;
                }
                
                setDaysOfWeek(newDaysOfWeek);
                setTimesPerDay(scheduleData.timesPerDay || 4);
                setStartTime(startTime);
                setEndTime(endTime);
                // Update timezone state with API-provided value or default to 'US'
                const apiTimezone = scheduleData.timezone || 'US';
                setTimezone(apiTimezone);
                setExecutionInterval(interval);
                updatePreview(newDaysOfWeek, scheduleData.timesPerDay || 4, startTime, endTime, scheduleData.timezone || 'US', interval);
            } else if (response.ok && !data.success) {
                // Specific case: status code 200 but success:false
                setError('Schedule not found for user ' + (user?.email || selectedEmail) + '.<br/> You can create one by saving a schedule.');
                setErrorType('neutral');
            } else {
                // All other error conditions
                setError('An error occurred while fetching schedule data. Please try again later.');
                setErrorType('error');
            }
        } catch (err) {
            // Don't show direct error messages to users
            // Show a user-friendly message instead
            setError('An error occurred while fetching schedule data. Please try again later.');
            setErrorType('error');
        } finally {
            setLoading(false);
        }
    };

    // Parse emails from parameters and update available emails
    const updateAvailableEmails = () => {
        if (parameters.userEmail) {
            const emails = parameters.userEmail.split(',').map(email => email.trim()).filter(email => email !== '');
            setAvailableEmails(emails);
            // Set the first email as selected by default if none is selected or if selected email is not in the list
            if (!selectedEmail || !emails.includes(selectedEmail)) {
                setSelectedEmail(emails[0] || '');
            }
        } else {
            setAvailableEmails([]);
            setSelectedEmail('');
        }
    };

    // Initialize the form when the component mounts
    useEffect(() => {
        // Always update available emails when parameters change
        updateAvailableEmails();
        
        // Fetch schedule data for the user's email when component mounts
        // We should always try to fetch fresh data from the API
        if (user && user.email) {
            fetchScheduleData(user.email);
        } else if (selectedEmail) {
            // Fallback to selectedEmail if user.email is not available
            fetchScheduleData(selectedEmail);
        }
    }, [parameters, user]);

    // Fetch schedule data when selected email changes
    useEffect(() => {
        console.log('Selected email changed to:', selectedEmail);
        if (selectedEmail) {
            fetchScheduleData(selectedEmail);
        }
    }, [selectedEmail]);

    // Auto-hide error after 2 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Calculate valid execution counts based on time range
    const calculateValidExecutions = (startTime, endTime) => {
        try {
            const [startHour, startMinute] = startTime.split(':').map(Number);
            const [endHour, endMinute] = endTime.split(':').map(Number);
            
            const startTotalMinutes = startHour * 60 + startMinute;
            let endTotalMinutes = endHour * 60 + endMinute;
            
            if (endTotalMinutes < startTotalMinutes) {
                endTotalMinutes += 24 * 60;
            }
            
            const totalDurationMinutes = endTotalMinutes - startTotalMinutes;
            
            // Limit to logical max (e.g., 24 hours = max 24 executions)
            const maxExecutions = 24; 
            const validExecutions = [];
            
            for (let count = 1; count <= maxExecutions; count++) {
                if (count === 1) {
                    validExecutions.push(1);
                    continue;
                }

                const stepSize = totalDurationMinutes / (count - 1);
                
                const generatedTimes = new Set();
                let isMathematicallySound = true;
                
                for (let i = 0; i < count; i++) {
                    // Apply the same Hourly Rounding logic here
                    let currentStepMinutes = i * stepSize;
                    const snappedToHour = Math.round(currentStepMinutes / 60) * 60;

                    if (generatedTimes.has(snappedToHour)) {
                        isMathematicallySound = false;
                        break;
                    }
                    generatedTimes.add(snappedToHour);
                }
                
                if (isMathematicallySound) {
                    validExecutions.push(count);
                }
            }
            
            return validExecutions;
        } catch (e) {
            console.error('Error calculating valid executions:', e);
            return [1, 2, 3, 4];
        }
    };
    
    // Update schedule preview
    const updatePreview = (days = daysOfWeek, times = timesPerDay, start = startTime, end = endTime, tz = timezone, interval = null) => {
        // Update execution days
        const selectedDays = Object.keys(days)
            .filter(day => days[day])
            .map(day => day.charAt(0).toUpperCase() + day.slice(1))
            .join(', ') || 'None selected';
        setExecutionDays(selectedDays);
        
        // Calculate scheduled times and actual execution count
        const { intervalText, scheduledTimesText, executionCount } = calculateScheduledTimes(start, end, times);
        
        // Update execution frequency with actual count
        setExecutionFrequency(`${executionCount} times per day`);
        
        // Use API-provided interval if available, otherwise use calculated interval
        if (interval) {
            setExecutionInterval(interval);
        } else {
            setExecutionInterval(intervalText);
        }
        
        setScheduledTimes(scheduledTimesText);
        
        // Update timezone display
        setTimezoneDisplay('Puerto Rico');
        
        // Update next execution time
        const nextExec = getNextExecutionTime();
        setNextExecution(nextExec);
    };

    // Calculate scheduled times based on dynamic formula
    const calculateScheduledTimes = (startTime, endTime, timesPerDay) => {
        if (!timesPerDay || timesPerDay <= 0) {
            return { intervalText: 'No executions', scheduledTimesText: 'None', executionCount: 0 };
        }
        
        try {
            const [startHour, startMinute] = startTime.split(':').map(Number);
            const [endHour, endMinute] = endTime.split(':').map(Number);
            
            const startTotalMinutes = startHour * 60 + startMinute;
            let endTotalMinutes = endHour * 60 + endMinute;
            
            if (endTotalMinutes < startTotalMinutes) {
                endTotalMinutes += 24 * 60;
            }
            
            const totalDurationMinutes = endTotalMinutes - startTotalMinutes;
            const stepSize = timesPerDay > 1 ? totalDurationMinutes / (timesPerDay - 1) : 0;
            
            const times = [];
            const usedTimes = new Set();
            
            for (let i = 0; i < timesPerDay; i++) {
                // MATH: Calculate raw minutes
                let currentStepMinutes = i * stepSize;
                
                // CRON FIX: Snap to the nearest Hour (60 minutes)
                // This forces 11:40 -> 12:00 to ensure a valid single cron expression
                currentStepMinutes = Math.round(currentStepMinutes / 60) * 60;

                const currentTimeMinutes = startTotalMinutes + currentStepMinutes;
                
                const hours = Math.floor(currentTimeMinutes / 60) % 24;
                // Minutes will always be 00 now, but we keep the logic generic
                const minutes = Math.round(currentTimeMinutes % 60);
                const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                
                if (!usedTimes.has(timeString)) {
                    times.push(timeString);
                    usedTimes.add(timeString);
                }
            }
            
            // Generate interval text
            let intervalText = 'Once';
            if (timesPerDay > 1) {
                // Show the average gap in hours
                const avgStepHours = Math.round(stepSize / 60);
                intervalText = `Approx. every ${avgStepHours} hr${avgStepHours > 1 ? 's' : ''}`;
            }
            
            return {
                intervalText: intervalText,
                scheduledTimesText: times.join(', '),
                executionCount: times.length
            };
        } catch (e) {
            console.error(e);
            return { intervalText: 'Error', scheduledTimesText: 'Error', executionCount: 0 };
        }
    };

    // Calculate interval minutes based on desired execution count
    const calculateIntervalMinutes = (startTime, endTime, timesPerDay) => {
        if (timesPerDay < 2) {
            return 0;
        }
        
        try {
            const [startHour, startMinute] = startTime.split(':').map(Number);
            const [endHour, endMinute] = endTime.split(':').map(Number);
            
            const startTotalMinutes = startHour * 60 + startMinute;
            let endTotalMinutes = endHour * 60 + endMinute;
            
            if (endTotalMinutes < startTotalMinutes) {
                endTotalMinutes += 24 * 60;
            }
            
            const totalMinutes = endTotalMinutes - startTotalMinutes;
            
            // THE FIX: Directly return the formula result (rounded)
            // Formula: Duration / (Count - 1)
            return Math.round(totalMinutes / (timesPerDay - 1));
            
        } catch (e) {
            console.error('Error calculating interval minutes:', e);
            return 60; 
        }
    };

    // Get next execution time
    const getNextExecutionTime = () => {
        const now = new Date();
        const currentTime = now.toTimeString().substring(0, 5);
        
        // If current time is before start time, next execution is today at start time
        if (currentTime < startTime) {
            return `Today at ${startTime}`;
        }
        
        // If current time is after end time, next execution is tomorrow at start time
        if (currentTime > endTime) {
            return `Tomorrow at ${startTime}`;
        }
        
        // Calculate scheduled times to determine next execution
        const { scheduledTimesText } = calculateScheduledTimes(startTime, endTime, timesPerDay);
        const times = scheduledTimesText.split(', ').map(time => time.trim());
        
        // Find the next time that is after the current time
        for (const time of times) {
            if (time > currentTime) {
                return `Today at ${time}`;
            }
        }
        
        // If no time is found after current time, return tomorrow at start time
        return `Tomorrow at ${startTime}`;
    };

    // Show status message with click-to-hide and auto-hide after 4 seconds
    const showStatus = (message, isSuccess = true) => {
        setStatusBar({
            show: true,
            message: message,
            isSuccess: isSuccess
        });
    };

    // Auto-hide status bar after 4 seconds
    useEffect(() => {
        if (statusBar.show) {
            const timer = setTimeout(() => {
                setStatusBar(prev => ({ ...prev, show: false }));
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [statusBar.show]);

    // Collect all schedule parameters from the form
    const collectSchedule = () => {
        const selectedDays = Object.keys(daysOfWeek).filter(day => daysOfWeek[day]);
        
        return {
            daysOfWeek: selectedDays,
            timesPerDay: timesPerDay,
            startTime: startTime,
            endTime: endTime,
            timezone: timezone
        };
    };

    // Validate schedule parameters
    const validateSchedule = (schedule) => {
        // Check if at least one day of the week is selected
        const hasSelectedDay = schedule.daysOfWeek && schedule.daysOfWeek.length > 0;
        if (!hasSelectedDay) {
            return "Please select at least one day of the week";
        }
        
        // Check if timesPerDay is a positive number
        if (schedule.timesPerDay <= 0) {
            return "Times per day must be a positive number";
        }
        
        // Check if timesPerDay is in the valid executions array
        const validExecutions = calculateValidExecutions(schedule.startTime, schedule.endTime);
        if (!validExecutions.includes(schedule.timesPerDay)) {
            return `Times per day must be one of the following values: ${validExecutions.join(', ')}`;
        }
        
        if (!schedule.startTime || !schedule.endTime) {
            return "Please specify start and end times";
        }
        
        // Check if start time is before end time
        if (schedule.startTime >= schedule.endTime) {
            return "Start time must be before end time";
        }
        
        return null;
    };

    // Handle day of week checkbox change
    const handleDayChange = (day) => {
        const newDaysOfWeek = {
            ...daysOfWeek,
            [day]: !daysOfWeek[day]
        };
        setDaysOfWeek(newDaysOfWeek);
        updatePreview(newDaysOfWeek);
    };

    // Handle times per day change
    const handleTimesPerDayChange = (value) => {
        setTimesPerDay(parseInt(value));
        updatePreview(undefined, parseInt(value));
    };

    // Handle start time change
    const handleStartTimeChange = (value) => {
        setStartTime(value);
        updatePreview(undefined, undefined, value);
    };

    // Handle end time change
    const handleEndTimeChange = (value) => {
        setEndTime(value);
        updatePreview(undefined, undefined, undefined, value);
    };

    // Handle timezone change
    const handleTimezoneChange = (value) => {
        setTimezone(value);
        updatePreview(undefined, undefined, undefined, undefined, value);
    };

    // Save schedule to context and send to external API
    const saveSchedule = async () => {
        const scheduleData = collectSchedule();
        const validationError = validateSchedule(scheduleData);
        
        if (validationError) {
            showStatus(validationError, false);
            return;
        }
        
        try {
            updateSchedule(scheduleData);
            
            // Send schedule data to external API
            const emailToSend = selectedEmail || (user && user.email) || '';
            console.log('emailToSend:', emailToSend);
            if (emailToSend) {
                // Prepare the data in the format expected by the external API
                const apiScheduleData = {
                    userEmail: emailToSend,
                    executionDays: Object.keys(daysOfWeek).filter(day => daysOfWeek[day]).map(day => day.toLowerCase()),
                    startTime: startTime,
                    endTime: endTime,
                    intervalMinutes: calculateIntervalMinutes(startTime, endTime, timesPerDay),
                    timezone: timezone
                };
                
                // Prepare headers with authorization
                const headers = {
                    'Content-Type': 'application/json',
                };
                
                // Add authorization header if user is available
                if (user && user.tokenType && user.accessToken) {
                    headers['Authorization'] = `${user.tokenType}${user.accessToken}`;
                }

                const response = await fetch(`/server/rest/ai-agent/schedule/${emailToSend}`, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify(apiScheduleData),
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    showStatus("Schedule saved successfully! The email processor will use these settings.");
                } else {
                    showStatus("Schedule saved locally but failed to send to external API: " + (data.message || 'Unknown error'), false);
                }
            } else {
                showStatus("Schedule saved successfully! The email processor will use these settings.");
            }
        } catch (e) {
            showStatus("Error saving schedule: " + e.message, false);
        }
    };

    // Reset to default schedule
    const resetToDefaults = () => {
        if (window.confirm("Are you sure you want to reset all schedule parameters to their default values?")) {
            // Reset checkboxes
            const newDaysOfWeek = {
                monday: true,
                tuesday: true,
                wednesday: true,
                thursday: true,
                friday: true,
                saturday: false,
                sunday: false
            };
            setDaysOfWeek(newDaysOfWeek);
            
            // Reset other values
            setTimesPerDay(defaultSchedule.timesPerDay);
            setStartTime(defaultSchedule.startTime);
            setEndTime(defaultSchedule.endTime);
            setTimezone(defaultSchedule.timezone);
            
            showStatus("Schedule reset to default values. Don't forget to save if you want to keep these settings.");
            updatePreview(newDaysOfWeek, defaultSchedule.timesPerDay, defaultSchedule.startTime, defaultSchedule.endTime, defaultSchedule.timezone);
        }
    };

    return (
        <div className="email-automation-container">
            <div className="header-container">
                <h1>Email Automation Dashboard</h1>
                <div className="nav-links">
                    <a href="/#/ai/automations/email" className={`nav-link ${location.pathname === '/ai/automations/email' ? 'active' : ''}`}>Email Dashboard</a>
                    <a href="/#/ai/automations/email/schedule" className={`nav-link ${location.pathname === '/ai/automations/email/schedule' ? 'active' : ''}`}>Schedule Configuration</a>
                    <a href="/#/ai/automations/email/params" className={`nav-link ${location.pathname === '/ai/automations/email/params' ? 'active' : ''}`}>Email Parameters</a>
                </div>
            </div>
            <div className="container">
                
                {loading && (
                    <div className="status-bar info">
                        <p>Loading schedule data...</p>
                    </div>
                )}
                
                {error && (
                    <div 
                        className={`status-bar ${errorType}`}
                        onClick={() => setError(null)}
                    >
                        <p dangerouslySetInnerHTML={{ __html: error }} />
                    </div>
                )}
                
                {statusBar.show && (
                    <div 
                        className={`status-bar ${statusBar.isSuccess ? 'success' : 'error'}`}
                        onClick={() => setStatusBar(prev => ({ ...prev, show: false }))}
                    >
                        <p>{statusBar.message}</p>
                    </div>
                )}
                
                <form>
                    <div className="section">
                        <h2 className="section-title section-title-with-help">
                            <span className="section-title-text">Schedule Settings</span>
                            <button
                                type="button"
                                className={`section-help-btn ${isScheduleSettingsHelpOpen ? 'active' : ''}`}
                                onClick={() => setIsScheduleSettingsHelpOpen(prev => !prev)}
                                aria-label={isScheduleSettingsHelpOpen ? 'Hide Schedule Settings help' : 'Show Schedule Settings help'}
                                aria-expanded={isScheduleSettingsHelpOpen}
                                title={isScheduleSettingsHelpOpen ? 'Hide Schedule Settings help' : 'Show Schedule Settings help'}
                            >
                                ?
                            </button>
                        </h2>
                        {isScheduleSettingsHelpOpen && (
                            <div className="section-help-panel">
                                <p>Schedule Settings defines when the email automation is allowed to run and which inbox schedule you are editing.</p>
                                <ul className="section-help-list">
                                    <li><strong>Select Email:</strong> Choose which configured email address you want to manage when multiple inboxes are available.</li>
                                    <li><strong>Days of Week:</strong> Pick the days when the automation is active.</li>
                                    <li><strong>Times Per Day:</strong> Select how many executions happen within the working window. Only valid options are shown.</li>
                                    <li><strong>Start Time / End Time:</strong> Set the first and last execution boundaries for each active day.</li>
                                    <li><strong>Timezone:</strong> Applies the timezone used for schedule calculations and preview display.</li>
                                </ul>
                            </div>
                        )}
                        {availableEmails.length > 0 && (
                            <div className="form-group">
                                <label htmlFor="selectedEmail">Select Email:</label>
                                <select
                                    id="selectedEmail"
                                    value={selectedEmail}
                                    onChange={(e) => setSelectedEmail(e.target.value)}
                                    className="email-select"
                                >
                                    {availableEmails.map((email, index) => (
                                        <option key={index} value={email}>{email}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="form-group">
                            <label>Days of Week:</label>
                            <div className="checkbox-group">
                                <div className="checkbox-item">
                                    <input 
                                        type="checkbox" 
                                        id="monday" 
                                        checked={daysOfWeek.monday}
                                        onChange={() => handleDayChange('monday')}
                                    />
                                    <label htmlFor="monday">Monday</label>
                                </div>
                                <div className="checkbox-item">
                                    <input 
                                        type="checkbox" 
                                        id="tuesday" 
                                        checked={daysOfWeek.tuesday}
                                        onChange={() => handleDayChange('tuesday')}
                                    />
                                    <label htmlFor="tuesday">Tuesday</label>
                                </div>
                                <div className="checkbox-item">
                                    <input 
                                        type="checkbox" 
                                        id="wednesday" 
                                        checked={daysOfWeek.wednesday}
                                        onChange={() => handleDayChange('wednesday')}
                                    />
                                    <label htmlFor="wednesday">Wednesday</label>
                                </div>
                                <div className="checkbox-item">
                                    <input 
                                        type="checkbox" 
                                        id="thursday" 
                                        checked={daysOfWeek.thursday}
                                        onChange={() => handleDayChange('thursday')}
                                    />
                                    <label htmlFor="thursday">Thursday</label>
                                </div>
                                <div className="checkbox-item">
                                    <input 
                                        type="checkbox" 
                                        id="friday" 
                                        checked={daysOfWeek.friday}
                                        onChange={() => handleDayChange('friday')}
                                    />
                                    <label htmlFor="friday">Friday</label>
                                </div>
                                <div className="checkbox-item">
                                    <input 
                                        type="checkbox" 
                                        id="saturday" 
                                        checked={daysOfWeek.saturday}
                                        onChange={() => handleDayChange('saturday')}
                                    />
                                    <label htmlFor="saturday">Saturday</label>
                                </div>
                                <div className="checkbox-item">
                                    <input 
                                        type="checkbox" 
                                        id="sunday" 
                                        checked={daysOfWeek.sunday}
                                        onChange={() => handleDayChange('sunday')}
                                    />
                                    <label htmlFor="sunday">Sunday</label>
                                </div>
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="timesPerDay">Times Per Day: <span id="timesPerDayValue">{timesPerDay}</span></label>
                            <div className="slider-container">
                                <select 
                                    id="timesPerDay"
                                    value={timesPerDay}
                                    onChange={(e) => handleTimesPerDayChange(e.target.value)}
                                    className="times-per-day-select"
                                >
                                    {calculateValidExecutions(startTime, endTime).map((times) => (
                                        <option key={times} value={times}>{times} time{times > 1 ? 's' : ''} per day</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="startTime">Start Time (First Execution):</label>
                            <input 
                                type="time" 
                                id="startTime" 
                                value={startTime}
                                onChange={(e) => handleStartTimeChange(e.target.value)}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="endTime">End Time (Last Execution):</label>
                            <input 
                                type="time" 
                                id="endTime" 
                                value={endTime}
                                onChange={(e) => handleEndTimeChange(e.target.value)}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="timezone">Timezone:</label>
                            <select 
                                id="timezone" 
                                value={timezone}
                                onChange={(e) => handleTimezoneChange(e.target.value)}
                            >
                                <option value="America/Puerto_Rico">America/Puerto Rico</option>
                            </select>
                        </div>
                    </div>
                    
                    <div className="section">
                        <h2 className="section-title section-title-with-help">
                            <span className="section-title-text">Schedule Preview</span>
                            <button
                                type="button"
                                className={`section-help-btn ${isSchedulePreviewHelpOpen ? 'active' : ''}`}
                                onClick={() => setIsSchedulePreviewHelpOpen(prev => !prev)}
                                aria-label={isSchedulePreviewHelpOpen ? 'Hide Schedule Preview help' : 'Show Schedule Preview help'}
                                aria-expanded={isSchedulePreviewHelpOpen}
                                title={isSchedulePreviewHelpOpen ? 'Hide Schedule Preview help' : 'Show Schedule Preview help'}
                            >
                                ?
                            </button>
                        </h2>
                        {isSchedulePreviewHelpOpen && (
                            <div className="section-help-panel">
                                <p>Schedule Preview turns your selections into a readable execution summary before you save the configuration.</p>
                                <ul className="section-help-list">
                                    <li><strong>Execution Days:</strong> Summarizes which days are currently active.</li>
                                    <li><strong>Times Per Day:</strong> Shows the number of runs that will be scheduled each active day.</li>
                                    <li><strong>Interval:</strong> Displays the estimated spacing between one execution and the next.</li>
                                    <li><strong>Scheduled Times:</strong> Lists the exact run times generated from the current settings.</li>
                                    <li><strong>Timezone:</strong> Shows the timezone used for the previewed schedule.</li>
                                </ul>
                            </div>
                        )}
                        <div id="scheduleInfo" className="schedule-info">
                            <div className="info-item">
                                <span className="info-label">Execution Days:</span> 
                                <span id="executionDays">{executionDays}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Times Per Day:</span> 
                                <span id="executionFrequency">{executionFrequency}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Interval:</span> 
                                <span id="executionInterval">{executionInterval}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Scheduled Times:</span> 
                                <span id="scheduledTimes">{scheduledTimes}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Timezone:</span> 
                                <span id="timezoneDisplay">{timezoneDisplay}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="actions">
                        <Button 
                            variant="contained" 
                            color="secondary" 
                            onClick={saveSchedule}
                            className="btn btn-secondary"
                        >
                            Save Schedule
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ScheduleConfig;
